import type { RouteRecordRaw } from 'vue-router'
import AdminDashboard from '@/views/admin/AdminDashboard.vue'
import QuestionBankList from '@/views/admin/QuestionBankList.vue'
import QuestionBankDetail from '@/views/admin/QuestionBankDetail.vue'
import QuestionBankForm from '@/views/admin/QuestionBankForm.vue'
import QuestionBankImport from '@/views/admin/QuestionBankImport.vue'
import QuestionManagement from '@/views/admin/QuestionManagement.vue'
import CaseQuestionManagement from '@/views/admin/CaseQuestionManagement.vue'
import CaseImport from '@/views/admin/CaseImport.vue'
import UserManagement from '@/views/admin/UserManagement.vue'
import UserStatisticsDashboard from '@/views/admin/UserStatisticsDashboard.vue'
import RoleManagement from '@/views/admin/RoleManagement.vue'
import FeedbackManagement from '@/views/admin/FeedbackManagement.vue'
import QuestionBankAnalytics from '@/views/admin/QuestionBankAnalytics.vue'
import RecommendationManagement from '@/views/admin/RecommendationManagement.vue'
import PermissionGuard from '@/components/PermissionGuard.vue'

export const adminRoutes: RouteRecordRaw[] = [
  {
    path: '/admin',
    name: 'AdminLayout',
    component: () => import('@/layouts/AdminLayout.vue'),
    meta: { requiresAuth: true, requiredRole: 'admin' },
    children: [
      // 管理中心仪表盘
      {
        path: '',
        name: 'AdminDashboard',
        component: AdminDashboard,
        meta: { title: '管理中心' }
      },
      // 题库管理
      {
        path: 'question-banks',
        name: 'QuestionBankList',
        component: QuestionBankList,
        meta: { title: '题库管理' }
      },
      {
        path: 'question-banks/new',
        name: 'QuestionBankCreate',
        component: QuestionBankForm,
        meta: { title: '创建题库' }
      },
      {
        path: 'question-banks/:id',
        name: 'QuestionBankDetail',
        component: QuestionBankDetail,
        meta: { title: '题库详情' }
      },
      {
        path: 'question-banks/:id/edit',
        name: 'QuestionBankEdit',
        component: QuestionBankForm,
        meta: { title: '编辑题库' }
      },
      {
        path: 'question-banks/import',
        name: 'QuestionBankImport',
        component: QuestionBankImport,
        meta: { title: '导入题库' }
      },
      // 案例题管理（材料 + 子题）
      {
        path: 'question-banks/:bankId/cases',
        name: 'CaseQuestionList',
        component: CaseQuestionManagement,
        meta: { title: '案例题管理' }
      },
      {
        path: 'question-banks/:bankId/cases/new',
        name: 'CaseQuestionCreate',
        component: CaseQuestionManagement,
        meta: { title: '新增案例题' }
      },
      {
        path: 'question-banks/:bankId/cases/:caseId/edit',
        name: 'CaseQuestionEdit',
        component: CaseQuestionManagement,
        meta: { title: '编辑案例题' }
      },
      {
        path: 'question-banks/import/case-study',
        name: 'CaseImport',
        component: CaseImport,
        meta: { title: '案例题导入' }
      },
      {
        path: 'question-bank-analytics',
        name: 'QuestionBankAnalytics',
        component: QuestionBankAnalytics,
        meta: { title: '题库分析' }
      },
      {
        path: 'recommendations',
        name: 'RecommendationManagement',
        component: RecommendationManagement,
        meta: { title: '精准推荐与归因' }
      },
      // 题目管理
      {
        path: 'question-banks/:bankId/questions/new',
        name: 'QuestionCreate',
        component: QuestionManagement,
        meta: { title: '创建题目' }
      },
      {
        path: 'question-banks/:bankId/questions/:questionId/edit',
        name: 'QuestionEdit',
        component: QuestionManagement,
        meta: { title: '编辑题目' }
      },
      // 用户管理
      {
        path: 'users',
        name: 'UserManagement',
        component: UserManagement,
        meta: { title: '用户管理' }
      },
      {
        path: 'user-statistics',
        name: 'UserStatisticsDashboard',
        component: UserStatisticsDashboard,
        meta: { title: '用户统计分析' }
      },
      // 角色管理
      {
        path: 'roles',
        name: 'RoleManagement',
        component: RoleManagement,
        meta: { title: '角色管理' }
      },
      // 反馈管理
      {
        path: 'feedbacks',
        name: 'FeedbackManagement',
        component: FeedbackManagement,
        meta: { title: '反馈管理' }
      }
    ]
  }
]

// 路由守卫
type RoleType = 'user' | 'admin' | 'super_admin'

interface RouteMeta {
  requiresAuth?: boolean
  requiredRole?: RoleType
  title?: string
}

export function setupAdminRouter(router: any) {
  router.beforeEach((to: any, from: any, next: any) => {
    const authStore = useAuthStore()
    
    // 检查是否需要认证
    if (to.meta.requiresAuth && !authStore.isAuthenticated) {
      next('/login')
    } else if (to.meta.requiredRole) {
      const userRole = (authStore.user?.role || 'user') as RoleType
      const roleHierarchy: Record<RoleType, number> = {
        user: 1,
        admin: 2,
        super_admin: 3
      }
      
      const requiredRole = to.meta.requiredRole as RoleType
      const requiredLevel = roleHierarchy[requiredRole] || 1
      const userLevel = roleHierarchy[userRole] || 1
      
      if (userLevel >= requiredLevel) {
        next()
      } else {
        next('/403')
      }
    } else {
      next()
    }
  })
}

// 辅助函数 - 需要实际导入useAuthStore
import { useAuthStore } from '@/stores/auth'
